import { useEffect, useState, useMemo, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import { FaCheck } from "react-icons/fa";
import "../../../Style/waterloo-payroll/index.css";
import { formatDate } from "Helper/Converters";
import { exportFileToExcel } from "Utils/utils";

const MULTIPLIER_FIELDS = ["mkStandard", "mkAdditional", "clStandard", "clAdditional"];

const getSectionsFromCategory = (category) => {
    if (!category?.sections || typeof category.sections !== "object")
        return [];
    return Object.entries(category.sections).sort(
        ([a], [b]) => (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0)
    );
};

const hasValue = (value) =>
    value !== undefined && value !== null && value !== "";

const calculateSubtotal = (total, multiplier) => {
    if (total === undefined || total === null || total === "") return 0;
    const totalNum = parseFloat(total) || 0;
    const mult = parseFloat(multiplier) || 0;
    const result = totalNum * mult;
    return isNaN(result) ? 0 : result;
};

const formatCurrency = (value) => {
    if (value === undefined || value === null || isNaN(value)) return "";
    return `$${value.toFixed(0)}`;
};

const normalizeSectionRow = (row) => {
    const totals = row?.totals || {};
    const stationCode =
        row?.station ||
        (Array.isArray(row?.stationCodes) ? row.stationCodes.join(" ") : "") ||
        "";

    return {
        station: stationCode,
        mkStandard: row?.mkStandard ?? totals?.mkStandard ?? 0,
        mkAdditional: row?.mkAdditional ?? totals?.mkAdditional ?? 0,
        clStandard: row?.clStandard ?? totals?.clStandard ?? 0,
        clAdditional: row?.clAdditional ?? totals?.clAdditional ?? 0,
    };
};

const emptyMultiplier = () => ({
    mkStandard: "",
    mkAdditional: "",
    clStandard: "",
    clAdditional: "",
});

const CATEGORIES = [
    { key: "mixed", label: "Mixed - Field Marked & Field Clear", shortLabel: "Mixed", color: "#0068af" },
    { key: "fieldClear", label: "Field Clear - Field Clear Only", shortLabel: "Field Clear", color: "#B33791" },
    { key: "marked", label: "Field Marked - Field Marked Only", shortLabel: "Marked", color: "#F57C00" },
];

const HaltonRegionPerTicketReport = ({ show, handleClose, data, user }) => {
    const [mixed, setMixed] = useState(null);
    const [fieldClear, setFieldClear] = useState(null);
    const [marked, setMarked] = useState(null);
    const [multipliers, setMultipliers] = useState({ mixed: {}, fieldClear: {}, marked: {} });
    const [showGrandTotal, setShowGrandTotal] = useState(false);
    const [savedMultipliers, setSavedMultipliers] = useState(null);

    const updateMultiplier = useCallback((category, sectionKey, field, value) => {
        setMultipliers((prev) => ({
            ...prev,
            [category]: {
                ...prev[category],
                [sectionKey]: {
                    ...(prev[category]?.[sectionKey] || emptyMultiplier()),
                    [field]: value,
                },
            },
        }));
    }, []);

    const validateGrandTotalGeneration = useCallback(() => {
        const categories = [
            { key: "mixed", data: mixed },
            { key: "fieldClear", data: fieldClear },
            { key: "marked", data: marked },
        ];
        for (const { key: category, data: categoryData } of categories) {
            const sectionEntries = getSectionsFromCategory(categoryData);
            const catMultipliers = multipliers[category] || {};
            for (const [sectionKey, sectionData] of sectionEntries) {
                const totals = sectionData?.totals;
                if (!totals) continue;
                const mult = catMultipliers[sectionKey] || emptyMultiplier();
                for (const field of MULTIPLIER_FIELDS) {
                    const totalValue = totals[field];
                    if (hasValue(totalValue) && (parseFloat(totalValue) || 0) > 0) {
                        if (!hasValue(mult[field])) return false;
                    }
                }
            }
        }
        return true;
    }, [mixed, fieldClear, marked, multipliers]);

    const getCurrentMultipliers = useCallback(
        () => ({
            mixed: { ...multipliers.mixed },
            fieldClear: { ...multipliers.fieldClear },
            marked: { ...multipliers.marked },
        }),
        [multipliers]
    );

    const hasMultipliersChanged = useCallback(() => {
        if (!savedMultipliers || !showGrandTotal) return true;
        const current = getCurrentMultipliers();
        const saved = savedMultipliers;
        const compare = (a, b) =>
            MULTIPLIER_FIELDS.some((f) => (a[f] || "") !== (b[f] || ""));
        for (const cat of ["mixed", "fieldClear", "marked"]) {
            const keys = new Set([
                ...Object.keys(current[cat] || {}),
                ...Object.keys(saved[cat] || {}),
            ]);
            for (const sectionKey of keys) {
                if (compare(current[cat]?.[sectionKey] || {}, saved[cat]?.[sectionKey] || {}))
                    return true;
            }
        }
        return false;
    }, [savedMultipliers, showGrandTotal, getCurrentMultipliers, multipliers]);

    const handleGenerateGrandTotal = () => {
        if (!validateGrandTotalGeneration()) {
            alert("Please fill all multiplier fields before generating grand total.");
            return;
        }
        setSavedMultipliers(getCurrentMultipliers());
        setShowGrandTotal(true);
    };

    const calculateCategoryGrandTotal = useCallback(
        (categoryData, categoryKey) => {
            if (!showGrandTotal || !categoryData) return null;
            const sectionEntries = getSectionsFromCategory(categoryData);
            const catMultipliers = multipliers[categoryKey] || {};
            const totals = { mkStandard: 0, mkAdditional: 0, clStandard: 0, clAdditional: 0 };
            for (const [sectionKey, sectionData] of sectionEntries) {
                const sectionTotals = sectionData?.totals;
                const mult = catMultipliers[sectionKey];
                if (!sectionTotals || !mult) continue;
                MULTIPLIER_FIELDS.forEach((field) => {
                    totals[field] += calculateSubtotal(sectionTotals[field], mult[field]);
                });
            }
            return totals;
        },
        [showGrandTotal, multipliers]
    );

    const mixedGrandTotal = useMemo(
        () => calculateCategoryGrandTotal(mixed, "mixed"),
        [mixed, calculateCategoryGrandTotal]
    );
    const fieldClearGrandTotal = useMemo(
        () => calculateCategoryGrandTotal(fieldClear, "fieldClear"),
        [fieldClear, calculateCategoryGrandTotal]
    );
    const markedGrandTotal = useMemo(
        () => calculateCategoryGrandTotal(marked, "marked"),
        [marked, calculateCategoryGrandTotal]
    );

    const resetAllStates = useCallback(() => {
        setMultipliers({ mixed: {}, fieldClear: {}, marked: {} });
        setShowGrandTotal(false);
        setSavedMultipliers(null);
    }, []);

    const handleModalClose = () => {
        resetAllStates();
        handleClose();
    };

    useEffect(() => {
        const region = data?.haltonRegion;
        const mixedData = region?.mixed ?? null;
        const fieldClearData = region?.fieldClear ?? null;
        const markedData = region?.marked ?? null;
        setMixed(mixedData);
        setFieldClear(fieldClearData);
        setMarked(markedData);

        const initial = { mixed: {}, fieldClear: {}, marked: {} };
        getSectionsFromCategory(mixedData).forEach(([sectionKey]) => {
            initial.mixed[sectionKey] = emptyMultiplier();
        });
        getSectionsFromCategory(fieldClearData).forEach(([sectionKey]) => {
            initial.fieldClear[sectionKey] = emptyMultiplier();
        });
        getSectionsFromCategory(markedData).forEach(([sectionKey]) => {
            initial.marked[sectionKey] = emptyMultiplier();
        });
        setMultipliers(initial);
        setShowGrandTotal(false);
        setSavedMultipliers(null);
    }, [data]);

    const hasAnyData = useMemo(() => {
        const mixedSections = getSectionsFromCategory(mixed);
        const fieldClearSections = getSectionsFromCategory(fieldClear);
        const markedSections = getSectionsFromCategory(marked);
        return mixedSections.length > 0 || fieldClearSections.length > 0 || markedSections.length > 0;
    }, [mixed, fieldClear, marked]);

    const formatCategoryDataForExcel = useCallback(
        (categoryData, categoryKey, categoryLabel, grandTotal) => {
            const sectionEntries = getSectionsFromCategory(categoryData);
            if (sectionEntries.length === 0) return [];

            const formattedData = [
                [
                    { v: "Station Code", t: "s", s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } } },
                    { v: "MK Standard", t: "s", s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } } },
                    { v: "MK Standard Additional", t: "s", s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } } },
                    { v: "CL Standard", t: "s", s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } } },
                    { v: "CL Standard Additional", t: "s", s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } } },
                ],
            ];

            const catMultipliers = multipliers[categoryKey] || {};
            for (const [sectionKey, sectionData] of sectionEntries) {
                const rows = sectionData?.rows || [];
                if (rows.length === 0) continue;
                const label = `${sectionKey} Station Code's`;
                formattedData.push([], [{ v: label, t: "s", s: { font: { bold: true } } }]);
                rows.forEach((row) => {
                    const normalizedRow = normalizeSectionRow(row);
                    formattedData.push([
                        { v: normalizedRow.station },
                        { v: normalizedRow.mkStandard },
                        { v: normalizedRow.mkAdditional },
                        { v: normalizedRow.clStandard },
                        { v: normalizedRow.clAdditional },
                    ]);
                });
                const totals = sectionData?.totals || {};
                const mult = catMultipliers[sectionKey] || emptyMultiplier();
                formattedData.push(
                    [
                        { v: "Total", t: "s", s: { font: { bold: true } } },
                        { v: totals.mkStandard ?? 0, s: { font: { bold: true } } },
                        { v: totals.mkAdditional ?? 0, s: { font: { bold: true } } },
                        { v: totals.clStandard ?? 0, s: { font: { bold: true } } },
                        { v: totals.clAdditional ?? 0, s: { font: { bold: true } } },
                    ],
                    [
                        { v: "Multiplier" },
                        { v: mult.mkStandard ?? "" },
                        { v: mult.mkAdditional ?? "" },
                        { v: mult.clStandard ?? "" },
                        { v: mult.clAdditional ?? "" },
                    ],
                    [
                        { v: "Sub Total", t: "s", s: { font: { bold: true } } },
                        { v: formatCurrency(calculateSubtotal(totals.mkStandard, mult.mkStandard)), s: { font: { bold: true } } },
                        { v: formatCurrency(calculateSubtotal(totals.mkAdditional, mult.mkAdditional)), s: { font: { bold: true } } },
                        { v: formatCurrency(calculateSubtotal(totals.clStandard, mult.clStandard)), s: { font: { bold: true } } },
                        { v: formatCurrency(calculateSubtotal(totals.clAdditional, mult.clAdditional)), s: { font: { bold: true } } },
                    ]
                );
            }

            if (grandTotal) {
                formattedData.push(
                    [],
                    [
                        { v: `${categoryLabel} Grand Total`, t: "s", s: { font: { bold: true } } },
                        { v: formatCurrency(grandTotal.mkStandard), s: { font: { bold: true } } },
                        { v: formatCurrency(grandTotal.mkAdditional), s: { font: { bold: true } } },
                        { v: formatCurrency(grandTotal.clStandard), s: { font: { bold: true } } },
                        { v: formatCurrency(grandTotal.clAdditional), s: { font: { bold: true } } },
                    ]
                );
            }
            return formattedData;
        },
        [multipliers]
    );

    const handleExportToExcel = () => {
        if (!showGrandTotal) {
            alert('Firstly, generate grand total by clicking on "Generate Grand Total" button');
            return;
        }
        const mixedData = formatCategoryDataForExcel(mixed, "mixed", "Mixed", mixedGrandTotal);
        const fieldClearData = formatCategoryDataForExcel(fieldClear, "fieldClear", "Field Clear", fieldClearGrandTotal);
        const markedData = formatCategoryDataForExcel(marked, "marked", "Marked", markedGrandTotal);
        const combinedData = [...mixedData, ...fieldClearData, ...markedData];

        const startDate = user?.startDate
            ? new Date(user.startDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
            : "";
        const endDate = user?.endDate
            ? new Date(user.endDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
            : "";
        const fileName = `${user?.name || "Per Tickets"} Report ${startDate}${endDate ? `-${endDate}` : ""}`;
        exportFileToExcel(combinedData, fileName);
    };

    const renderCategorySection = (categoryData, categoryKey, title, headerBgColor) => {
        const sectionEntries = getSectionsFromCategory(categoryData);
        if (sectionEntries.length === 0) return null;

        return (
            <>
                <div
                    style={{
                        marginTop: categoryKey === "mixed" ? "1rem" : "2rem",
                        marginBottom: "1rem",
                        backgroundColor: headerBgColor,
                        color: "white",
                        padding: "12px 16px",
                        borderRadius: "4px",
                        fontWeight: "600",
                        fontSize: "18px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                >
                    {title}
                </div>
                {sectionEntries.map(([sectionKey, sectionData]) => {
                    const rows = sectionData?.rows || [];
                    const totals = sectionData?.totals || {};
                    const mult = multipliers[categoryKey]?.[sectionKey] || emptyMultiplier();
                    const label = `${sectionKey} Station Code's`;

                    return (
                        <table key={`${categoryKey}-${sectionKey}`} className="payroll-table">
                            <thead>
                                <tr>
                                    <th style={headerBgColor ? { backgroundColor: headerBgColor, color: "white" } : undefined}>
                                        {label}
                                    </th>
                                    <th style={headerBgColor ? { backgroundColor: headerBgColor, color: "white" } : undefined}>
                                        MK Standard
                                    </th>
                                    <th style={headerBgColor ? { backgroundColor: headerBgColor, color: "white" } : undefined}>
                                        MK Standard Additional
                                    </th>
                                    <th style={headerBgColor ? { backgroundColor: headerBgColor, color: "white" } : undefined}>
                                        CL Standard
                                    </th>
                                    <th style={headerBgColor ? { backgroundColor: headerBgColor, color: "white" } : undefined}>
                                        CL Standard Additional
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, index) => {
                                    const normalizedRow = normalizeSectionRow(row);
                                    return (
                                        <tr key={index}>
                                            <td>{normalizedRow.station}</td>
                                            <td>{normalizedRow.mkStandard}</td>
                                            <td>{normalizedRow.mkAdditional}</td>
                                            <td>{normalizedRow.clStandard}</td>
                                            <td>{normalizedRow.clAdditional}</td>
                                        </tr>
                                    );
                                })}
                                <tr className="total-row">
                                    <td style={{ fontWeight: "bold" }}>Total</td>
                                    <td>{totals?.mkStandard}</td>
                                    <td>{totals?.mkAdditional}</td>
                                    <td>{totals?.clStandard}</td>
                                    <td>{totals?.clAdditional}</td>
                                </tr>
                                <tr className="multiplier-row">
                                    <td>Multiplier</td>
                                    {MULTIPLIER_FIELDS.map((field) => (
                                        <td key={field}>
                                            <input
                                                type="number"
                                                value={mult[field]}
                                                onChange={(e) => updateMultiplier(categoryKey, sectionKey, field, e.target.value)}
                                                disabled={!hasValue(totals?.[field])}
                                            />
                                        </td>
                                    ))}
                                </tr>
                                <tr className="subtotal-row">
                                    <td style={{ fontWeight: "bold" }}>Sub Total</td>
                                    <td>
                                        {formatCurrency(calculateSubtotal(totals?.mkStandard, mult.mkStandard))}
                                    </td>
                                    <td>
                                        {formatCurrency(calculateSubtotal(totals?.mkAdditional, mult.mkAdditional))}
                                    </td>
                                    <td>
                                        {formatCurrency(calculateSubtotal(totals?.clStandard, mult.clStandard))}
                                    </td>
                                    <td>
                                        {formatCurrency(calculateSubtotal(totals?.clAdditional, mult.clAdditional))}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    );
                })}
            </>
        );
    };

    const renderGrandTotalSection = (grandTotal, title, headerBgColor) => {
        if (!showGrandTotal || !grandTotal) return null;
        return (
            <div style={{ marginTop: "3rem" }}>
                <div
                    style={{
                        marginBottom: "1rem",
                        backgroundColor: headerBgColor,
                        color: "white",
                        padding: "12px 16px",
                        borderRadius: "4px",
                        fontWeight: "600",
                        fontSize: "18px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                >
                    {title}
                </div>
                <table className="payroll-table">
                    <thead>
                        <tr>
                            <th style={headerBgColor ? { backgroundColor: headerBgColor, color: "white" } : undefined}></th>
                            <th style={headerBgColor ? { backgroundColor: headerBgColor, color: "white" } : undefined}>
                                MK Standard
                            </th>
                            <th style={headerBgColor ? { backgroundColor: headerBgColor, color: "white" } : undefined}>
                                MK Standard Additional
                            </th>
                            <th style={headerBgColor ? { backgroundColor: headerBgColor, color: "white" } : undefined}>
                                CL Standard
                            </th>
                            <th style={headerBgColor ? { backgroundColor: headerBgColor, color: "white" } : undefined}>
                                CL Standard Additional
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={{ fontWeight: "bold" }}>Grand Total</td>
                            <td style={{ fontWeight: "bold" }}>{formatCurrency(grandTotal.mkStandard)}</td>
                            <td style={{ fontWeight: "bold" }}>{formatCurrency(grandTotal.mkAdditional)}</td>
                            <td style={{ fontWeight: "bold" }}>{formatCurrency(grandTotal.clStandard)}</td>
                            <td style={{ fontWeight: "bold" }}>{formatCurrency(grandTotal.clAdditional)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <Modal show={show} onHide={handleModalClose} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    Per Ticket Report - {user?.name} {" ("}
                    {formatDate(new Date(user?.endDate), "dd/MM/yyyy")} -{" "}
                    {formatDate(new Date(user?.startDate), "dd/MM/yyyy")}
                    {")"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: "1rem", overflowX: "auto" }}>
                {!data || !hasAnyData ? (
                    <div className="text-center p-4">
                        <p>No data available for the Per Ticket Report.</p>
                        <p className="text-muted">
                            Please ensure the report has been generated successfully.
                        </p>
                    </div>
                ) : (
                    <div className="payroll-table-container" style={{ width: "100%" }}>
                        {mixed && renderCategorySection(mixed, "mixed", CATEGORIES[0].label, CATEGORIES[0].color)}
                        {fieldClear &&
                            renderCategorySection(
                                fieldClear,
                                "fieldClear",
                                CATEGORIES[1].label,
                                CATEGORIES[1].color
                            )}
                        {marked &&
                            renderCategorySection(
                                marked,
                                "marked",
                                CATEGORIES[2].label,
                                CATEGORIES[2].color
                            )}

                        {showGrandTotal && mixedGrandTotal && mixed &&
                            renderGrandTotalSection(mixedGrandTotal, "Mixed Grand Total", CATEGORIES[0].color)}
                        {showGrandTotal && fieldClearGrandTotal && fieldClear && (
                            <div style={{ marginTop: "2rem" }}>
                                {renderGrandTotalSection(
                                    fieldClearGrandTotal,
                                    "Field Clear Grand Total",
                                    CATEGORIES[1].color
                                )}
                            </div>
                        )}
                        {showGrandTotal && markedGrandTotal && marked && (
                            <div style={{ marginTop: "2rem" }}>
                                {renderGrandTotalSection(
                                    markedGrandTotal,
                                    "Marked Grand Total",
                                    CATEGORIES[2].color
                                )}
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                {!showGrandTotal || hasMultipliersChanged() ? (
                    <Button onClick={handleGenerateGrandTotal} variant="primary">
                        Generate Grand Total
                    </Button>
                ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <FaCheck style={{ color: "green", fontSize: "18px" }} />
                        <span style={{ color: "green", fontWeight: "500" }}>
                            Grand Total Generated
                        </span>
                    </div>
                )}
                {showGrandTotal && !hasMultipliersChanged() && (
                    <Button onClick={handleExportToExcel} variant="primary">
                        Export
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default HaltonRegionPerTicketReport;
